import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import Channel from "../../../cp/components/Comet/Channel";
import {getConnection, statisticWatchStart, statisticWatchStop} from "../../../api/player";
import { getStreamMeta, getStreamSessions } from "../../../common/src/stream";
import {usePlayerContext} from "./PlayerContext";
import {isEpisodeRecorded, isStreamRecord} from "../utils/stream";
import {getStreamDay} from "../../../player/js/tools";
import { useEventContext } from "./EventContext";
import { useUserContext } from "./UserContext";

export const StatisticContext = createContext(null);

export const useStatisticContext = () => useContext(StatisticContext);

export const StatisticContextProvider = ({children}) => {
    const { eventKey } = useEventContext();
    const [cometChannel, setCometChannel] = useState(null);
    const [searchParams] = useSearchParams();
    const { userCometChannel, setUserCometChannel, setUserRole, setTokenBlocked } = useUserContext();

    useEffect(() => {
        if (!eventKey) {
            return;
        }

        const params = {
            eventKey,
            referrer: window.parent === window ? document.location.href : document.referrer,
            pageReferrer: document.referrer,
        };
        searchParams.forEach((value, key) => {
            if (key.substring(0, 4) === 'utm_') {
                params[key] = value;
            } else if(key === 'embedLocation') {
                params.referrer = value;
            } else if(key === 'referrer') {
                params.pageReferrer = value;
            }
        });

        getConnection(params)
            .then(({cometChannel, role}) => {
                setCometChannel(cometChannel);
                setUserRole(role);
            })
            .catch((e) => {
                if (e?.status === 406) {
                    setTokenBlocked(true);
                    setUserCometChannel(null);
                }
            })
        ;
    }, [eventKey]);

    const context = {
        cometChannel,
    };

    return (
        <StatisticContext.Provider value={context}>
            {cometChannel && <Channel name={cometChannel} />}
            {userCometChannel && <Channel name={userCometChannel} />}
            <PlayerStatisticWatcher />
            {children}
        </StatisticContext.Provider>
    );
}

const PlayerStatisticWatcher = () => {
    const {cometChannel} = useStatisticContext();
    const {userCometChannel} = useUserContext();
    const {currentTime, duration, stream, streamDay, episode} = usePlayerContext();
    const watcherRef = useRef(null);
    const userWatcherRef = useRef(null);

    useEffect(() => {
        if (!cometChannel) {
            return;
        }

        watcherRef.current = new StatisticWatcher(cometChannel);
    }, [cometChannel]);

    useEffect(() => {
        if (!userCometChannel) {
            return;
        }

        userWatcherRef.current = new StatisticWatcher(userCometChannel);
    }, [userCometChannel]);

    useEffect(() => {
        if (typeof watcherRef.current !== 'function') {
            return;
        }

        watcherRef.current(currentTime, duration, stream, streamDay, episode);
    }, [currentTime, duration, stream, streamDay, episode]);
}

function StatisticWatcher(cometChannel) {
    let currentSession = null;
    let lastSource = null;
    let lastTime = null;

    const createWatchSession = (stream, episode, timeStart, streamDay) => {
        let promiseReject = null;
        let startTimer = null;
        const delay = 10;

        const startPromise = new Promise((resolve, reject) => {
            promiseReject = reject;

            startTimer = setTimeout(() => {
                startTimer = null;
                const params = {
                    episodeId: episode?.id,
                    cometChannel,
                    timeStart,
                    streamDay,
                    offset: isStreamRecord(stream) ? stream?.offset : 0,
                };
                console.debug('startWatch', timeStart, params);
                return statisticWatchStart(stream.id, params).then(resolve);
            }, delay * 1000);
        }).catch(() => {
            clearTimeout(startTimer);
        })

        return (timeStop) => {
            startPromise
                .then(({id}) => {
                    console.debug('stopWatch', timeStop);
                    return statisticWatchStop(id, {timeStop});
                })
                .catch(() => {
                    console.debug('watchSession not saved', {timeStart, timeStop});
                })
            ;
            if (Math.abs(timeStop - timeStart) < delay && startTimer) {
                promiseReject();
            }
        }
    }
    const startWatchSession = (stream, episode, timeStart, streamDay) => {
        stopWatchSession(lastTime);
        currentSession = createWatchSession(stream, episode, timeStart, streamDay);
    }
    const stopWatchSession = (timeStop) => {
        if (typeof currentSession !== 'function') {
            return;
        }
        currentSession(timeStop);
        currentSession = null;
    }

    return (currentTime, duration, stream, streamDay, episode) => {
        let time = currentTime;
        let currentSource = stream;
        let currentEpisode = null;

        if (isNaN(currentTime)) {
            return;
        }

        if (stream && episode && isEpisodeRecorded(episode)) {
            currentSource = episode;
            currentEpisode = episode;
        }
        if (stream && !stream.dvr) {
            time = duration;
        }

        const { isRecord } = getStreamMeta(stream);
        const sessions = getStreamSessions(stream);

        if (stream && streamDay > 0 && !isRecord) {
            time += sessions.reduce((time, session) => {
                if (getStreamDay(stream.timeStart, session.timeStart) < streamDay) {
                    return time + (session.timeStop - session.timeStart);
                }

                return time;
            }, 0);
        }

        if (lastSource !== currentSource) {
            if (lastSource) {
                stopWatchSession(lastTime);
            }

            if (currentSource) {
                startWatchSession(stream, currentEpisode, time, streamDay);
            }

            lastSource = currentSource;
        } else if (currentSource && (time < lastTime || time > lastTime + 10)) {
            stopWatchSession(lastTime);
            startWatchSession(stream, currentEpisode, time, streamDay);
        }

        lastTime = time;
    }
}
